import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BreadcrumbJsonLd, ItemListJsonLd } from "../components/JsonLd";
import { formatReadingTime } from "../lib/readingTime";
import { ArrowRight, Flame, Eye, Calendar, Clock, Trophy } from "lucide-react";

export default function BlogTop() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles?sort=views&limit=10")
      .then(r => setArticles(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = "Top 10 des articles les plus lus · Fedevo 2.0";
    return () => { document.title = prev; };
  }, []);

  const ranked = articles.filter(a => (a.views || 0) > 0);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12" data-testid="blog-top-page">
      <BreadcrumbJsonLd items={[
        { name: "Accueil", path: "/" },
        { name: "Blog", path: "/blog" },
        { name: "Top 10", path: "/blog/top" },
      ]} />
      <ItemListJsonLd
        name="Top 10 articles les plus lus · Fedevo 2.0"
        description="Classement des articles les plus consultés sur Fedevo 2.0 — analyses, portraits et reportages de culture urbaine."
        items={ranked.map(a => ({ name: a.title, path: `/blog/${a.slug}`, image: a.cover_image, description: a.excerpt }))}
      />

      <div className="mb-10">
        <Link to="/blog" className="inline-block mb-4 text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] hover:text-[color:var(--accent)]">
          ← Retour au blog
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <Trophy className="w-7 h-7 text-[color:var(--accent)]" />
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)]">Classement</div>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none">Top 10 · Le + lu</h1>
        <p className="text-[color:var(--text-muted)] mt-4 max-w-2xl">
          Le palmarès en temps réel des articles les plus lus sur Fedevo 2.0. Ordre mis à jour à chaque lecture.
        </p>
      </div>

      {loading && (
        <div className="py-20 text-center text-[color:var(--text-muted)]">Chargement du classement...</div>
      )}

      {!loading && ranked.length === 0 && (
        <div className="py-20 text-center text-[color:var(--text-muted)]" data-testid="blog-top-empty">
          Pas encore assez de vues pour établir un classement. Revenez bientôt !
        </div>
      )}

      <div className="space-y-4 stagger" data-testid="blog-top-list">
        {ranked.map((a, i) => (
          <Link
            to={`/blog/${a.slug}`}
            key={a.id}
            data-testid={`top-card-${a.slug}`}
            className="group card-hover rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] flex items-stretch gap-0 overflow-hidden"
          >
            <div
              className={`shrink-0 w-20 sm:w-28 flex items-center justify-center font-display text-5xl sm:text-7xl ${
                i === 0
                  ? "bg-gradient-to-br from-orange-500 to-red-500 text-white"
                  : i === 1
                  ? "bg-gradient-to-br from-yellow-400 to-orange-400 text-black"
                  : i === 2
                  ? "bg-gradient-to-br from-amber-700 to-amber-500 text-white"
                  : "bg-[color:var(--bg)] text-[color:var(--text-muted)] border-r border-[color:var(--border)]"
              }`}
            >
              {String(i + 1).padStart(2, "0")}
            </div>

            {a.cover_image && (
              <div className="shrink-0 hidden sm:block w-40 md:w-56 bg-black overflow-hidden">
                <img src={a.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}

            <div className="flex-1 p-4 sm:p-5 min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-2 flex-wrap">
                <Calendar className="w-3 h-3" />
                {new Date(a.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatReadingTime(a.content)}
                </span>
                {a.tags?.[0] && (
                  <span className="px-2 py-0.5 rounded-full bg-[color:var(--bg)] border border-[color:var(--border)] normal-case">
                    {a.tags[0]}
                  </span>
                )}
                <span className="ml-auto inline-flex items-center gap-1 text-[color:var(--accent)] font-semibold">
                  <Eye className="w-3 h-3" /> {a.views} vue{a.views > 1 ? "s" : ""}
                </span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-2 line-clamp-2 group-hover:text-[color:var(--accent)] transition-colors">
                {a.title}
              </h2>
              <p className="hidden sm:block text-sm text-[color:var(--text-muted)] line-clamp-2">{a.excerpt}</p>
              <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--accent)]">
                Lire <ArrowRight className="w-3.5 h-3.5" />
                {i === 0 && (
                  <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] uppercase tracking-widest font-bold rounded">
                    <Flame className="w-3 h-3" /> Le + lu
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
